'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateContentModerationFindingStatus,
  provenanceKeys,
  type ContentModerationFindingSummary,
  type DetectionType,
  type FindingStatusValue,
} from '@/shared/cors-provenance-api';
import { useCellServiceSession } from './useCellServiceSession';
import { useContentModerationFolderNameResolver } from './useContentModerationFolderName';

export type UpdateFindingStatusVars = {
  fileMd5: string;
  status: FindingStatusValue;
  /**
   * Case the finding belongs to, as the nav holds it (a display name). The hook
   * resolves it to the folder segment before sending — findings are keyed per
   * case on that segment, so the same document flagged in two cases has one
   * finding each. Also scopes cache invalidation after success.
   */
  caseId: string;
};

/**
 * TanStack ``useMutation`` hook for
 * ``PUT /provenance-service/v1/content-moderation/findings/{detectionType}/{uuid}/status``.
 *
 * On success the hook invalidates the findings list and summary for the
 * affected case, plus the Evidence Locker's provenance tree / files queries
 * so the next locker render refetches ``/tree`` and ``/tree/search`` with
 * the updated ``findingStatus`` (those queries otherwise stay fresh for
 * up to 5 minutes). Callers are responsible for optimistic UI updates
 * (e.g. via a local ``statusOverrides`` map) and reverting them in the
 * ``onError`` callback.
 *
 * Auth wiring follows the same pattern as every other cell-service hook.
 */
export function useUpdateFindingStatus(detectionType: DetectionType = 'CSAM') {
  const { cellId, accessToken } = useCellServiceSession();
  const resolveFolderName = useContentModerationFolderNameResolver();
  const queryClient = useQueryClient();

  return useMutation<ContentModerationFindingSummary, Error, UpdateFindingStatusVars>({
    mutationFn: ({ fileMd5, caseId, status }) => {
      const folderName = resolveFolderName(caseId);
      if (!folderName) {
        // Refuse rather than fall back to the display name. A review decision
        // written under the wrong key is worse than a failed one: the finding
        // stays FLAGGED, and the counters it should have moved never move.
        throw new Error('Could not resolve this case folder. Please try again.');
      }
      return updateContentModerationFindingStatus({
        cellId: cellId as string,
        accessToken: accessToken as string,
        detectionType,
        fileMd5,
        caseId: folderName,
        status,
      });
    },
    onSuccess: (_, { caseId }) => {
      // Re-resolve rather than thread it through: this is a cache read of a list
      // whose folder segments do not change, and the mutation just succeeded with
      // it, so it cannot be null here.
      const folderName = resolveFolderName(caseId);
      // Invalidate all findings pages for this case (any detection type / status filter).
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'findings', cellId, folderName],
      });
      // Invalidate the summary so the counter cards refresh (any detection type).
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'summary', cellId, folderName],
      });
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'finding-detail', cellId, folderName],
      });
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'user-flagged'],
      });
      // Evidence Locker rows read ``findingStatus`` off tree nodes (and the
      // files sidecar as a fallback). Prefix-match ``tree()`` covers both
      // ``/tree`` list pages and ``/tree/search``.
      queryClient.invalidateQueries({ queryKey: provenanceKeys.tree() });
      queryClient.invalidateQueries({ queryKey: provenanceKeys.files() });
    },
  });
}
